// Chemistry Calculators helpers: molecular weight/molar mass, percent
// composition, empirical/molecular formula, moles<->grams, molarity,
// dilution, ideal gas law, pH/pOH, an atomic-number/element lookup tool,
// density, and limiting reactant/theoretical yield.
//
// Scope notes (deliberate narrowing, matching this project's convention of
// keeping formulas deterministic rather than handling every edge case):
// - The formula parser supports simple parenthesized groups (Ca(OH)2,
//   Fe2(SO4)3) but not hydrate dot-notation (CuSO4.5H2O) or charges/ions.
// - Empirical formula ratios are rounded to the nearest whole number
//   (no x2/x3 "still not integer" correction pass).
// - Element "name" and "category" in ELEMENT_TABLE are in English for all
//   118 elements; symbols are IUPAC-standard and language-invariant. Only
//   the small, finite set of *category* labels (metal, nonmetal, etc.) are
//   localized per-language in the lookup tool's output, since translating
//   all 118 proper element names accurately is out of scope here.
import { t } from './i18n'

// ============================================================
// Periodic table data (all 118 elements): symbol -> { name, atomicNumber,
// mass, category }. Standard atomic weights (conventional values, ~3-6
// significant figures); synthetic/highly radioactive elements use the
// mass number of their most stable known isotope.
// ============================================================
type ElementCategory =
    | 'nonmetal' | 'noble_gas' | 'alkali_metal' | 'alkaline_earth_metal'
    | 'transition_metal' | 'post_transition_metal' | 'metalloid' | 'halogen'
    | 'lanthanide' | 'actinide' | 'unknown'

type ElementData = { name: string; atomicNumber: number; mass: number; category: ElementCategory }

export const ELEMENT_TABLE: Record<string, ElementData> = {
    H: { name: 'Hydrogen', atomicNumber: 1, mass: 1.008, category: 'nonmetal' },
    He: { name: 'Helium', atomicNumber: 2, mass: 4.0026, category: 'noble_gas' },
    Li: { name: 'Lithium', atomicNumber: 3, mass: 6.94, category: 'alkali_metal' },
    Be: { name: 'Beryllium', atomicNumber: 4, mass: 9.0122, category: 'alkaline_earth_metal' },
    B: { name: 'Boron', atomicNumber: 5, mass: 10.81, category: 'metalloid' },
    C: { name: 'Carbon', atomicNumber: 6, mass: 12.011, category: 'nonmetal' },
    N: { name: 'Nitrogen', atomicNumber: 7, mass: 14.007, category: 'nonmetal' },
    O: { name: 'Oxygen', atomicNumber: 8, mass: 15.999, category: 'nonmetal' },
    F: { name: 'Fluorine', atomicNumber: 9, mass: 18.998, category: 'halogen' },
    Ne: { name: 'Neon', atomicNumber: 10, mass: 20.180, category: 'noble_gas' },
    Na: { name: 'Sodium', atomicNumber: 11, mass: 22.990, category: 'alkali_metal' },
    Mg: { name: 'Magnesium', atomicNumber: 12, mass: 24.305, category: 'alkaline_earth_metal' },
    Al: { name: 'Aluminium', atomicNumber: 13, mass: 26.982, category: 'post_transition_metal' },
    Si: { name: 'Silicon', atomicNumber: 14, mass: 28.085, category: 'metalloid' },
    P: { name: 'Phosphorus', atomicNumber: 15, mass: 30.974, category: 'nonmetal' },
    S: { name: 'Sulfur', atomicNumber: 16, mass: 32.06, category: 'nonmetal' },
    Cl: { name: 'Chlorine', atomicNumber: 17, mass: 35.45, category: 'halogen' },
    Ar: { name: 'Argon', atomicNumber: 18, mass: 39.948, category: 'noble_gas' },
    K: { name: 'Potassium', atomicNumber: 19, mass: 39.098, category: 'alkali_metal' },
    Ca: { name: 'Calcium', atomicNumber: 20, mass: 40.078, category: 'alkaline_earth_metal' },
    Sc: { name: 'Scandium', atomicNumber: 21, mass: 44.956, category: 'transition_metal' },
    Ti: { name: 'Titanium', atomicNumber: 22, mass: 47.867, category: 'transition_metal' },
    V: { name: 'Vanadium', atomicNumber: 23, mass: 50.942, category: 'transition_metal' },
    Cr: { name: 'Chromium', atomicNumber: 24, mass: 51.996, category: 'transition_metal' },
    Mn: { name: 'Manganese', atomicNumber: 25, mass: 54.938, category: 'transition_metal' },
    Fe: { name: 'Iron', atomicNumber: 26, mass: 55.845, category: 'transition_metal' },
    Co: { name: 'Cobalt', atomicNumber: 27, mass: 58.933, category: 'transition_metal' },
    Ni: { name: 'Nickel', atomicNumber: 28, mass: 58.693, category: 'transition_metal' },
    Cu: { name: 'Copper', atomicNumber: 29, mass: 63.546, category: 'transition_metal' },
    Zn: { name: 'Zinc', atomicNumber: 30, mass: 65.38, category: 'transition_metal' },
    Ga: { name: 'Gallium', atomicNumber: 31, mass: 69.723, category: 'post_transition_metal' },
    Ge: { name: 'Germanium', atomicNumber: 32, mass: 72.630, category: 'metalloid' },
    As: { name: 'Arsenic', atomicNumber: 33, mass: 74.922, category: 'metalloid' },
    Se: { name: 'Selenium', atomicNumber: 34, mass: 78.971, category: 'nonmetal' },
    Br: { name: 'Bromine', atomicNumber: 35, mass: 79.904, category: 'halogen' },
    Kr: { name: 'Krypton', atomicNumber: 36, mass: 83.798, category: 'noble_gas' },
    Rb: { name: 'Rubidium', atomicNumber: 37, mass: 85.468, category: 'alkali_metal' },
    Sr: { name: 'Strontium', atomicNumber: 38, mass: 87.62, category: 'alkaline_earth_metal' },
    Y: { name: 'Yttrium', atomicNumber: 39, mass: 88.906, category: 'transition_metal' },
    Zr: { name: 'Zirconium', atomicNumber: 40, mass: 91.224, category: 'transition_metal' },
    Nb: { name: 'Niobium', atomicNumber: 41, mass: 92.906, category: 'transition_metal' },
    Mo: { name: 'Molybdenum', atomicNumber: 42, mass: 95.95, category: 'transition_metal' },
    Tc: { name: 'Technetium', atomicNumber: 43, mass: 98, category: 'transition_metal' },
    Ru: { name: 'Ruthenium', atomicNumber: 44, mass: 101.07, category: 'transition_metal' },
    Rh: { name: 'Rhodium', atomicNumber: 45, mass: 102.906, category: 'transition_metal' },
    Pd: { name: 'Palladium', atomicNumber: 46, mass: 106.42, category: 'transition_metal' },
    Ag: { name: 'Silver', atomicNumber: 47, mass: 107.868, category: 'transition_metal' },
    Cd: { name: 'Cadmium', atomicNumber: 48, mass: 112.414, category: 'transition_metal' },
    In: { name: 'Indium', atomicNumber: 49, mass: 114.818, category: 'post_transition_metal' },
    Sn: { name: 'Tin', atomicNumber: 50, mass: 118.710, category: 'post_transition_metal' },
    Sb: { name: 'Antimony', atomicNumber: 51, mass: 121.760, category: 'metalloid' },
    Te: { name: 'Tellurium', atomicNumber: 52, mass: 127.60, category: 'metalloid' },
    I: { name: 'Iodine', atomicNumber: 53, mass: 126.904, category: 'halogen' },
    Xe: { name: 'Xenon', atomicNumber: 54, mass: 131.293, category: 'noble_gas' },
    Cs: { name: 'Caesium', atomicNumber: 55, mass: 132.905, category: 'alkali_metal' },
    Ba: { name: 'Barium', atomicNumber: 56, mass: 137.327, category: 'alkaline_earth_metal' },
    La: { name: 'Lanthanum', atomicNumber: 57, mass: 138.905, category: 'lanthanide' },
    Ce: { name: 'Cerium', atomicNumber: 58, mass: 140.116, category: 'lanthanide' },
    Pr: { name: 'Praseodymium', atomicNumber: 59, mass: 140.908, category: 'lanthanide' },
    Nd: { name: 'Neodymium', atomicNumber: 60, mass: 144.242, category: 'lanthanide' },
    Pm: { name: 'Promethium', atomicNumber: 61, mass: 145, category: 'lanthanide' },
    Sm: { name: 'Samarium', atomicNumber: 62, mass: 150.36, category: 'lanthanide' },
    Eu: { name: 'Europium', atomicNumber: 63, mass: 151.964, category: 'lanthanide' },
    Gd: { name: 'Gadolinium', atomicNumber: 64, mass: 157.25, category: 'lanthanide' },
    Tb: { name: 'Terbium', atomicNumber: 65, mass: 158.925, category: 'lanthanide' },
    Dy: { name: 'Dysprosium', atomicNumber: 66, mass: 162.500, category: 'lanthanide' },
    Ho: { name: 'Holmium', atomicNumber: 67, mass: 164.930, category: 'lanthanide' },
    Er: { name: 'Erbium', atomicNumber: 68, mass: 167.259, category: 'lanthanide' },
    Tm: { name: 'Thulium', atomicNumber: 69, mass: 168.934, category: 'lanthanide' },
    Yb: { name: 'Ytterbium', atomicNumber: 70, mass: 173.045, category: 'lanthanide' },
    Lu: { name: 'Lutetium', atomicNumber: 71, mass: 174.967, category: 'lanthanide' },
    Hf: { name: 'Hafnium', atomicNumber: 72, mass: 178.49, category: 'transition_metal' },
    Ta: { name: 'Tantalum', atomicNumber: 73, mass: 180.948, category: 'transition_metal' },
    W: { name: 'Tungsten', atomicNumber: 74, mass: 183.84, category: 'transition_metal' },
    Re: { name: 'Rhenium', atomicNumber: 75, mass: 186.207, category: 'transition_metal' },
    Os: { name: 'Osmium', atomicNumber: 76, mass: 190.23, category: 'transition_metal' },
    Ir: { name: 'Iridium', atomicNumber: 77, mass: 192.217, category: 'transition_metal' },
    Pt: { name: 'Platinum', atomicNumber: 78, mass: 195.084, category: 'transition_metal' },
    Au: { name: 'Gold', atomicNumber: 79, mass: 196.967, category: 'transition_metal' },
    Hg: { name: 'Mercury', atomicNumber: 80, mass: 200.592, category: 'transition_metal' },
    Tl: { name: 'Thallium', atomicNumber: 81, mass: 204.38, category: 'post_transition_metal' },
    Pb: { name: 'Lead', atomicNumber: 82, mass: 207.2, category: 'post_transition_metal' },
    Bi: { name: 'Bismuth', atomicNumber: 83, mass: 208.980, category: 'post_transition_metal' },
    Po: { name: 'Polonium', atomicNumber: 84, mass: 209, category: 'post_transition_metal' },
    At: { name: 'Astatine', atomicNumber: 85, mass: 210, category: 'halogen' },
    Rn: { name: 'Radon', atomicNumber: 86, mass: 222, category: 'noble_gas' },
    Fr: { name: 'Francium', atomicNumber: 87, mass: 223, category: 'alkali_metal' },
    Ra: { name: 'Radium', atomicNumber: 88, mass: 226, category: 'alkaline_earth_metal' },
    Ac: { name: 'Actinium', atomicNumber: 89, mass: 227, category: 'actinide' },
    Th: { name: 'Thorium', atomicNumber: 90, mass: 232.038, category: 'actinide' },
    Pa: { name: 'Protactinium', atomicNumber: 91, mass: 231.036, category: 'actinide' },
    U: { name: 'Uranium', atomicNumber: 92, mass: 238.029, category: 'actinide' },
    Np: { name: 'Neptunium', atomicNumber: 93, mass: 237, category: 'actinide' },
    Pu: { name: 'Plutonium', atomicNumber: 94, mass: 244, category: 'actinide' },
    Am: { name: 'Americium', atomicNumber: 95, mass: 243, category: 'actinide' },
    Cm: { name: 'Curium', atomicNumber: 96, mass: 247, category: 'actinide' },
    Bk: { name: 'Berkelium', atomicNumber: 97, mass: 247, category: 'actinide' },
    Cf: { name: 'Californium', atomicNumber: 98, mass: 251, category: 'actinide' },
    Es: { name: 'Einsteinium', atomicNumber: 99, mass: 252, category: 'actinide' },
    Fm: { name: 'Fermium', atomicNumber: 100, mass: 257, category: 'actinide' },
    Md: { name: 'Mendelevium', atomicNumber: 101, mass: 258, category: 'actinide' },
    No: { name: 'Nobelium', atomicNumber: 102, mass: 259, category: 'actinide' },
    Lr: { name: 'Lawrencium', atomicNumber: 103, mass: 266, category: 'actinide' },
    Rf: { name: 'Rutherfordium', atomicNumber: 104, mass: 267, category: 'transition_metal' },
    Db: { name: 'Dubnium', atomicNumber: 105, mass: 268, category: 'transition_metal' },
    Sg: { name: 'Seaborgium', atomicNumber: 106, mass: 269, category: 'transition_metal' },
    Bh: { name: 'Bohrium', atomicNumber: 107, mass: 270, category: 'transition_metal' },
    Hs: { name: 'Hassium', atomicNumber: 108, mass: 269, category: 'transition_metal' },
    Mt: { name: 'Meitnerium', atomicNumber: 109, mass: 278, category: 'unknown' },
    Ds: { name: 'Darmstadtium', atomicNumber: 110, mass: 281, category: 'unknown' },
    Rg: { name: 'Roentgenium', atomicNumber: 111, mass: 282, category: 'unknown' },
    Cn: { name: 'Copernicium', atomicNumber: 112, mass: 285, category: 'transition_metal' },
    Nh: { name: 'Nihonium', atomicNumber: 113, mass: 286, category: 'post_transition_metal' },
    Fl: { name: 'Flerovium', atomicNumber: 114, mass: 289, category: 'post_transition_metal' },
    Mc: { name: 'Moscovium', atomicNumber: 115, mass: 290, category: 'post_transition_metal' },
    Lv: { name: 'Livermorium', atomicNumber: 116, mass: 293, category: 'post_transition_metal' },
    Ts: { name: 'Tennessine', atomicNumber: 117, mass: 294, category: 'halogen' },
    Og: { name: 'Oganesson', atomicNumber: 118, mass: 294, category: 'noble_gas' },
}

// ============================================================
// Shared helpers: formula parsing & pretty subscript/superscript display
// ============================================================
const SUBSCRIPT_DIGITS: Record<string, string> = { '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄', '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉' }
const SUPERSCRIPT_DIGITS: Record<string, string> = { '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹', '-': '⁻' }

export function toSubscriptFormula(formula: string): string {
    return formula.replace(/\d+/g, (match) => match.split('').map((d) => SUBSCRIPT_DIGITS[d] || d).join(''))
}

function toSuperscript(text: string): string {
    return text.split('').map((c) => SUPERSCRIPT_DIGITS[c] || c).join('')
}

// Parses formulas like "H2O", "C6H12O6", "Ca(OH)2", "Fe2(SO4)3" into
// element -> atom-count maps. Returns null on any parse/unknown-element error.
export function parseFormula(formula: string): Record<string, number> | null {
    const s = String(formula ?? '').replace(/\s+/g, '')
    if (!s) return null
    let i = 0

    function parseGroup(): Record<string, number> {
        const counts: Record<string, number> = {}
        while (i < s.length && s[i] !== ')') {
            if (s[i] === '(') {
                i++
                const inner = parseGroup()
                if (s[i] !== ')') throw new Error('mismatched parentheses')
                i++
                const numMatch = /^\d+/.exec(s.slice(i))
                const multiplier = numMatch ? parseInt(numMatch[0], 10) : 1
                if (numMatch) i += numMatch[0].length
                for (const [el, c] of Object.entries(inner)) {
                    counts[el] = (counts[el] || 0) + c * multiplier
                }
            } else {
                const m = /^[A-Z][a-z]?/.exec(s.slice(i))
                if (!m) throw new Error('invalid formula')
                const symbol = m[0]
                if (!ELEMENT_TABLE[symbol]) throw new Error(`unknown element ${symbol}`)
                i += symbol.length
                const numMatch = /^\d+/.exec(s.slice(i))
                const count = numMatch ? parseInt(numMatch[0], 10) : 1
                if (numMatch) i += numMatch[0].length
                counts[symbol] = (counts[symbol] || 0) + count
            }
        }
        return counts
    }

    try {
        const counts = parseGroup()
        if (i !== s.length || Object.keys(counts).length === 0) return null
        return counts
    } catch {
        return null
    }
}

export function molarMassOf(counts: Record<string, number>): number {
    return Object.entries(counts).reduce((sum, [el, count]) => sum + count * ELEMENT_TABLE[el].mass, 0)
}

const INVALID_FORMULA_LABEL: Record<string, string> = {
    en: 'Invalid formula', ru: 'Некорректная формула', lv: 'Nederīga formula', pl: 'Nieprawidłowy wzór',
    es: 'Fórmula no válida', fr: 'Formule invalide', it: 'Formula non valida', de: 'Ungültige Formel',
}
const TOTAL_MOLAR_MASS_LABEL: Record<string, string> = {
    en: 'Total molar mass', ru: 'Суммарная молярная масса', lv: 'Kopējā molārā masa', pl: 'Całkowita masa molowa',
    es: 'Masa molar total', fr: 'Masse molaire totale', it: 'Massa molare totale', de: 'Gesamte Molmasse',
}

// ============================================================
// 1. Molecular Weight Calculator
// ============================================================
export function molecularWeightCalculator(params: { formula: unknown; language: unknown }): {
    molar_mass: number | string; formatted_formula: string; steps: string
} {
    const formula = String(params.formula || '')
    const counts = parseFormula(formula)
    if (!counts) {
        const errText = t(INVALID_FORMULA_LABEL, params.language)
        return { molar_mass: errText, formatted_formula: formula, steps: errText }
    }
    const molarMass = molarMassOf(counts)
    const totalLabel = t(TOTAL_MOLAR_MASS_LABEL, params.language)
    const lines = Object.entries(counts).map(([el, count]) => {
        const mass = ELEMENT_TABLE[el].mass
        return `${el}: ${count} × ${mass} = ${(count * mass).toFixed(3)} g/mol`
    })
    lines.push(`${totalLabel} = ${molarMass.toFixed(3)} g/mol`)
    return { molar_mass: molarMass, formatted_formula: toSubscriptFormula(formula), steps: lines.join('\n') }
}

// ============================================================
// 2. Percent Composition Calculator
// ============================================================
export function percentCompositionCalculator(params: { formula: unknown; language: unknown }): {
    formatted_formula: string; molar_mass: number | string; composition: string; steps: string
} {
    const formula = String(params.formula || '')
    const counts = parseFormula(formula)
    if (!counts) {
        const errText = t(INVALID_FORMULA_LABEL, params.language)
        return { formatted_formula: formula, molar_mass: errText, composition: errText, steps: errText }
    }
    const molarMass = molarMassOf(counts)
    const totalLabel = t(TOTAL_MOLAR_MASS_LABEL, params.language)
    const compositionLines: string[] = []
    const stepLines: string[] = []
    for (const [el, count] of Object.entries(counts)) {
        const mass = ELEMENT_TABLE[el].mass
        const elementMass = count * mass
        const percent = (elementMass / molarMass) * 100
        compositionLines.push(`${el}: ${percent.toFixed(2)}%`)
        stepLines.push(`${el}: (${count} × ${mass}) / ${molarMass.toFixed(3)} × 100 = ${percent.toFixed(2)}%`)
    }
    stepLines.push(`${totalLabel} = ${molarMass.toFixed(3)} g/mol`)
    return {
        formatted_formula: toSubscriptFormula(formula),
        molar_mass: molarMass,
        composition: compositionLines.join('\n'),
        steps: stepLines.join('\n'),
    }
}

// ============================================================
// 3. Empirical Formula Calculator
// ============================================================
const MOLES_PER_100G_LABEL: Record<string, string> = {
    en: 'moles per 100 g', ru: 'моль на 100 г', lv: 'moli uz 100 g', pl: 'moli na 100 g',
    es: 'moles por 100 g', fr: 'moles pour 100 g', it: 'moli per 100 g', de: 'Mol pro 100 g',
}
const DIVIDE_BY_SMALLEST_LABEL: Record<string, string> = {
    en: 'Divide each by the smallest', ru: 'Делим каждое на наименьшее', lv: 'Dala katru ar mazāko', pl: 'Podziel każdą przez najmniejszą',
    es: 'Divide cada uno entre el menor', fr: 'Divisez chacun par le plus petit', it: 'Dividi ciascuno per il più piccolo', de: 'Jeden Wert durch den kleinsten teilen',
}

export function empiricalFormulaCalculator(params: {
    element_1: unknown; percent_1: unknown; element_2: unknown; percent_2: unknown; element_3: unknown; percent_3: unknown; language: unknown
}): { empirical_formula: string; steps: string } {
    const entries: Array<{ symbol: string; percent: number }> = []
    const rawPairs: Array<[unknown, unknown]> = [[params.element_1, params.percent_1], [params.element_2, params.percent_2], [params.element_3, params.percent_3]]
    for (const [rawSymbol, rawPercent] of rawPairs) {
        const symbol = String(rawSymbol || '').trim()
        const percent = Number(rawPercent) || 0
        if (symbol && percent > 0 && ELEMENT_TABLE[symbol]) entries.push({ symbol, percent })
    }
    if (entries.length < 2) {
        const errText = t(INVALID_FORMULA_LABEL, params.language)
        return { empirical_formula: errText, steps: errText }
    }

    const molesLabel = t(MOLES_PER_100G_LABEL, params.language)
    const moles = entries.map((e) => ({ symbol: e.symbol, mol: e.percent / ELEMENT_TABLE[e.symbol].mass }))
    const smallest = Math.min(...moles.map((m) => m.mol))
    const ratios = moles.map((m) => ({ symbol: m.symbol, ratio: Math.max(1, Math.round(m.mol / smallest)) }))

    const stepLines = moles.map((m) => `${m.symbol}: ${entries.find((e) => e.symbol === m.symbol)!.percent}% / ${ELEMENT_TABLE[m.symbol].mass} = ${m.mol.toFixed(4)} ${molesLabel}`)
    stepLines.push(`${t(DIVIDE_BY_SMALLEST_LABEL, params.language)} (${smallest.toFixed(4)}): ${ratios.map((r) => `${r.symbol} = ${r.ratio}`).join(', ')}`)

    const formulaStr = ratios.map((r) => `${r.symbol}${r.ratio > 1 ? r.ratio : ''}`).join('')
    return { empirical_formula: toSubscriptFormula(formulaStr), steps: stepLines.join('\n') }
}

// ============================================================
// 4. Molecular Formula Calculator (from Empirical Formula + Molar Mass)
// ============================================================
const MULTIPLIER_LABEL: Record<string, string> = {
    en: 'Multiplier', ru: 'Множитель', lv: 'Reizinātājs', pl: 'Mnożnik', es: 'Multiplicador', fr: 'Multiplicateur', it: 'Moltiplicatore', de: 'Multiplikator',
}
const EMPIRICAL_MASS_LABEL: Record<string, string> = {
    en: 'Empirical formula mass', ru: 'Масса эмпирической формулы', lv: 'Empīriskās formulas masa', pl: 'Masa wzoru empirycznego',
    es: 'Masa de la fórmula empírica', fr: 'Masse de la formule empirique', it: 'Massa della formula empirica', de: 'Masse der empirischen Formel',
}

export function molecularFormulaCalculator(params: { empirical_formula: unknown; molar_mass: unknown; language: unknown }): {
    molecular_formula: string; multiplier: number | string; empirical_formula_mass: number | string; steps: string
} {
    const formula = String(params.empirical_formula || '')
    const targetMolarMass = Math.max(Number(params.molar_mass) || 0, 0.01)
    const counts = parseFormula(formula)
    if (!counts) {
        const errText = t(INVALID_FORMULA_LABEL, params.language)
        return { molecular_formula: errText, multiplier: errText, empirical_formula_mass: errText, steps: errText }
    }
    const empiricalMass = molarMassOf(counts)
    const multiplier = Math.max(1, Math.round(targetMolarMass / empiricalMass))
    const newCounts: Record<string, number> = {}
    for (const [el, count] of Object.entries(counts)) newCounts[el] = count * multiplier
    const molecularFormulaStr = Object.entries(newCounts).map(([el, count]) => `${el}${count > 1 ? count : ''}`).join('')

    const steps = [
        `${t(EMPIRICAL_MASS_LABEL, params.language)} = ${empiricalMass.toFixed(3)} g/mol`,
        `${t(MULTIPLIER_LABEL, params.language)} = round(${targetMolarMass.toFixed(3)} / ${empiricalMass.toFixed(3)}) = ${multiplier}`,
    ].join('\n')

    return { molecular_formula: toSubscriptFormula(molecularFormulaStr), multiplier, empirical_formula_mass: empiricalMass, steps }
}

// ============================================================
// 5. Moles <-> Grams Calculator
// ============================================================
export function molesGramsCalculator(params: { formula: unknown; mode: unknown; value: unknown; language: unknown }): {
    result: number | string; molar_mass: number | string; formatted_formula: string; steps: string
} {
    const formula = String(params.formula || '')
    const counts = parseFormula(formula)
    if (!counts) {
        const errText = t(INVALID_FORMULA_LABEL, params.language)
        return { result: errText, molar_mass: errText, formatted_formula: formula, steps: errText }
    }
    const molarMass = molarMassOf(counts)
    const value = Math.max(Number(params.value) || 0, 0)
    const mode = String(params.mode || 'grams_to_moles')
    const result = mode === 'moles_to_grams' ? value * molarMass : value / molarMass

    const steps = mode === 'moles_to_grams'
        ? `${value} mol × ${molarMass.toFixed(3)} g/mol = ${result.toFixed(4)} g`
        : `${value} g / ${molarMass.toFixed(3)} g/mol = ${result.toFixed(4)} mol`

    return { result, molar_mass: molarMass, formatted_formula: toSubscriptFormula(formula), steps }
}

// ============================================================
// 6. Molarity Calculator (M = mol / L)
// ============================================================
export function molarityCalculator(params: { solve_for: unknown; moles: unknown; volume_liters: unknown; molarity: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'molarity')
    const moles = Math.max(Number(params.moles) || 0, 0)
    const volume = Math.max(Number(params.volume_liters) || 0.0001, 0.0001)
    const molarity = Math.max(Number(params.molarity) || 0, 0)

    if (solveFor === 'moles') {
        const result = molarity * volume
        return { result, steps: `moles = M × V = ${molarity} × ${volume} = ${result.toFixed(4)} mol` }
    }
    if (solveFor === 'volume') {
        const result = moles / Math.max(molarity, 0.0001)
        return { result, steps: `V = mol / M = ${moles} / ${molarity} = ${result.toFixed(4)} L` }
    }
    const result = moles / volume
    return { result, steps: `M = mol / V = ${moles} / ${volume} = ${result.toFixed(4)} mol/L` }
}

// ============================================================
// 7. Dilution Calculator (C1V1 = C2V2)
// ============================================================
export function dilutionCalculator(params: { solve_for: unknown; c1: unknown; v1: unknown; c2: unknown; v2: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'v2')
    const c1 = Math.max(Number(params.c1) || 0, 0)
    const v1 = Math.max(Number(params.v1) || 0, 0)
    const c2 = Math.max(Number(params.c2) || 0, 0)
    const v2 = Math.max(Number(params.v2) || 0, 0)

    if (solveFor === 'c1') {
        const result = (c2 * v2) / Math.max(v1, 0.0001)
        return { result, steps: `C1 = (C2 × V2) / V1 = (${c2} × ${v2}) / ${v1} = ${result.toFixed(4)} mol/L` }
    }
    if (solveFor === 'v1') {
        const result = (c2 * v2) / Math.max(c1, 0.0001)
        return { result, steps: `V1 = (C2 × V2) / C1 = (${c2} × ${v2}) / ${c1} = ${result.toFixed(4)} L` }
    }
    if (solveFor === 'c2') {
        const result = (c1 * v1) / Math.max(v2, 0.0001)
        return { result, steps: `C2 = (C1 × V1) / V2 = (${c1} × ${v1}) / ${v2} = ${result.toFixed(4)} mol/L` }
    }
    const result = (c1 * v1) / Math.max(c2, 0.0001)
    return { result, steps: `V2 = (C1 × V1) / C2 = (${c1} × ${v1}) / ${c2} = ${result.toFixed(4)} L` }
}

// ============================================================
// 8. Ideal Gas Law Calculator (PV = nRT)
// ============================================================
const GAS_CONSTANT_R = 0.082057 // L·atm / (mol·K)

export function idealGasLawCalculator(params: {
    solve_for: unknown; pressure_atm: unknown; volume_liters: unknown; moles: unknown; temperature_k: unknown; language: unknown
}): { result: number | string; steps: string } {
    const solveFor = String(params.solve_for || 'p')
    const p = Math.max(Number(params.pressure_atm) || 0, 0)
    const v = Math.max(Number(params.volume_liters) || 0.0001, 0.0001)
    const n = Math.max(Number(params.moles) || 0, 0)
    const tK = Math.max(Number(params.temperature_k) || 0.0001, 0.0001)

    if (solveFor === 'v') {
        const result = (n * GAS_CONSTANT_R * tK) / Math.max(p, 0.0001)
        return { result, steps: `V = nRT / P = (${n} × ${GAS_CONSTANT_R} × ${tK}) / ${p} = ${result.toFixed(4)} L` }
    }
    if (solveFor === 'n') {
        const result = (p * v) / (GAS_CONSTANT_R * tK)
        return { result, steps: `n = PV / RT = (${p} × ${v}) / (${GAS_CONSTANT_R} × ${tK}) = ${result.toFixed(4)} mol` }
    }
    if (solveFor === 't') {
        const result = (p * v) / (n * GAS_CONSTANT_R) || 0
        return { result, steps: `T = PV / nR = (${p} × ${v}) / (${n} × ${GAS_CONSTANT_R}) = ${result.toFixed(4)} K` }
    }
    const result = (n * GAS_CONSTANT_R * tK) / v
    return { result, steps: `P = nRT / V = (${n} × ${GAS_CONSTANT_R} × ${tK}) / ${v} = ${result.toFixed(4)} atm` }
}

// ============================================================
// 9. pH & pOH Calculator
// ============================================================
const ACIDIC_LABEL: Record<string, string> = {
    en: 'Acidic', ru: 'Кислая среда', lv: 'Skāba', pl: 'Kwasowy', es: 'Ácido', fr: 'Acide', it: 'Acido', de: 'Sauer',
}
const BASIC_LABEL: Record<string, string> = {
    en: 'Basic (alkaline)', ru: 'Щелочная среда', lv: 'Bāziska (sārmaina)', pl: 'Zasadowy', es: 'Básico (alcalino)', fr: 'Basique (alcalin)', it: 'Basico (alcalino)', de: 'Basisch (alkalisch)',
}
const NEUTRAL_LABEL: Record<string, string> = {
    en: 'Neutral', ru: 'Нейтральная среда', lv: 'Neitrāla', pl: 'Obojętny', es: 'Neutro', fr: 'Neutre', it: 'Neutro', de: 'Neutral',
}

export function phCalculator(params: { input_type: unknown; value: unknown; language: unknown }): {
    ph: number | string; poh: number | string; h_concentration: string; oh_concentration: string; acidity: string
} {
    const inputType = String(params.input_type || 'pH')
    const value = Number(params.value)
    let ph: number
    if (inputType === 'pH') ph = value
    else if (inputType === 'pOH') ph = 14 - value
    else if (inputType === 'H_concentration') ph = -Math.log10(Math.max(value, 1e-20))
    else ph = 14 + Math.log10(Math.max(value, 1e-20)) // OH_concentration

    const poh = 14 - ph
    const hConc = Math.pow(10, -ph)
    const ohConc = Math.pow(10, -poh)

    const acidity = ph < 6.5 ? t(ACIDIC_LABEL, params.language) : ph > 7.5 ? t(BASIC_LABEL, params.language) : t(NEUTRAL_LABEL, params.language)

    const formatSci = (n: number): string => {
        const exp = Math.floor(Math.log10(n))
        const mantissa = n / Math.pow(10, exp)
        return `${mantissa.toFixed(2)} × 10${toSuperscript(String(exp))} mol/L`
    }

    return { ph, poh, h_concentration: formatSci(hConc), oh_concentration: formatSci(ohConc), acidity }
}

// ============================================================
// 10. Atomic Number & Element Lookup Tool
// ============================================================
const NOT_FOUND_LABEL: Record<string, string> = {
    en: 'Not found', ru: 'Не найдено', lv: 'Nav atrasts', pl: 'Nie znaleziono', es: 'No encontrado', fr: 'Introuvable', it: 'Non trovato', de: 'Nicht gefunden',
}
const CATEGORY_LABELS: Record<ElementCategory, Record<string, string>> = {
    nonmetal: { en: 'Nonmetal', ru: 'Неметалл', lv: 'Nemetāls', pl: 'Niemetal', es: 'No metal', fr: 'Non-métal', it: 'Non metallo', de: 'Nichtmetall' },
    noble_gas: { en: 'Noble Gas', ru: 'Благородный газ', lv: 'Cēlgāze', pl: 'Gaz szlachetny', es: 'Gas noble', fr: 'Gaz noble', it: 'Gas nobile', de: 'Edelgas' },
    alkali_metal: { en: 'Alkali Metal', ru: 'Щелочной металл', lv: 'Sārmu metāls', pl: 'Metal alkaliczny', es: 'Metal alcalino', fr: 'Métal alcalin', it: 'Metallo alcalino', de: 'Alkalimetall' },
    alkaline_earth_metal: { en: 'Alkaline Earth Metal', ru: 'Щёлочноземельный металл', lv: 'Sārmzemju metāls', pl: 'Metal ziem alkalicznych', es: 'Metal alcalinotérreo', fr: 'Métal alcalino-terreux', it: 'Metallo alcalino-terroso', de: 'Erdalkalimetall' },
    transition_metal: { en: 'Transition Metal', ru: 'Переходный металл', lv: 'Pārejas metāls', pl: 'Metal przejściowy', es: 'Metal de transición', fr: 'Métal de transition', it: 'Metallo di transizione', de: 'Übergangsmetall' },
    post_transition_metal: { en: 'Post-Transition Metal', ru: 'Постпереходный металл', lv: 'Pēcpārejas metāls', pl: 'Metal potransicyjny', es: 'Metal post-transición', fr: 'Métal post-transition', it: 'Metallo post-transizione', de: 'Post-Übergangsmetall' },
    metalloid: { en: 'Metalloid', ru: 'Металлоид', lv: 'Metaloīds', pl: 'Metaloid', es: 'Metaloide', fr: 'Métalloïde', it: 'Metalloide', de: 'Halbmetall' },
    halogen: { en: 'Halogen', ru: 'Галоген', lv: 'Halogēns', pl: 'Halogen', es: 'Halógeno', fr: 'Halogène', it: 'Alogeno', de: 'Halogen' },
    lanthanide: { en: 'Lanthanide', ru: 'Лантаноид', lv: 'Lantanoīds', pl: 'Lantanowiec', es: 'Lantánido', fr: 'Lanthanide', it: 'Lantanide', de: 'Lanthanoid' },
    actinide: { en: 'Actinide', ru: 'Актиноид', lv: 'Aktinoīds', pl: 'Aktynowiec', es: 'Actínido', fr: 'Actinide', it: 'Attinide', de: 'Actinoid' },
    unknown: { en: 'Unknown Properties', ru: 'Свойства не определены', lv: 'Nezināmas īpašības', pl: 'Nieznane właściwości', es: 'Propiedades desconocidas', fr: 'Propriétés inconnues', it: 'Proprietà sconosciute', de: 'Unbekannte Eigenschaften' },
}

export function elementLookupTool(params: { query: unknown; language: unknown }): {
    symbol: string; name: string; atomic_number: number | string; atomic_mass: number | string; category: string
} {
    const query = String(params.query || '').trim()
    const notFound = t(NOT_FOUND_LABEL, params.language)

    let match: [string, ElementData] | undefined
    const asNumber = Number(query)
    if (query && !isNaN(asNumber)) {
        match = Object.entries(ELEMENT_TABLE).find(([, data]) => data.atomicNumber === Math.round(asNumber))
    }
    if (!match && query) {
        const exactSymbol = Object.entries(ELEMENT_TABLE).find(([symbol]) => symbol.toLowerCase() === query.toLowerCase())
        match = exactSymbol
    }
    if (!match && query) {
        const byName = Object.entries(ELEMENT_TABLE).find(([, data]) => data.name.toLowerCase() === query.toLowerCase())
        match = byName
    }

    if (!match) {
        return { symbol: notFound, name: notFound, atomic_number: notFound, atomic_mass: notFound, category: notFound }
    }
    const [symbol, data] = match
    return {
        symbol,
        name: data.name,
        atomic_number: data.atomicNumber,
        atomic_mass: data.mass,
        category: t(CATEGORY_LABELS[data.category], params.language),
    }
}

// ============================================================
// 11. Density Calculator (chemistry: mass / volume)
// ============================================================
export function densityCalculatorChem(params: { solve_for: unknown; mass_grams: unknown; volume_ml: unknown; density: unknown; language: unknown }): {
    result: number | string; steps: string
} {
    const solveFor = String(params.solve_for || 'density')
    const mass = Math.max(Number(params.mass_grams) || 0, 0)
    const volume = Math.max(Number(params.volume_ml) || 0.0001, 0.0001)
    const density = Math.max(Number(params.density) || 0.0001, 0.0001)

    if (solveFor === 'mass') {
        const result = density * volume
        return { result, steps: `mass = ρ × V = ${density} × ${volume} = ${result.toFixed(4)} g` }
    }
    if (solveFor === 'volume') {
        const result = mass / density
        return { result, steps: `V = mass / ρ = ${mass} / ${density} = ${result.toFixed(4)} mL` }
    }
    const result = mass / volume
    return { result, steps: `ρ = mass / V = ${mass} / ${volume} = ${result.toFixed(4)} g/mL` }
}

// ============================================================
// 12. Limiting Reactant & Theoretical Yield Calculator
// ============================================================
const LIMITING_REACTANT_LABEL: Record<string, string> = {
    en: 'Limiting reactant', ru: 'Лимитирующий реагент', lv: 'Ierobežojošais reaģents', pl: 'Reagent ograniczający',
    es: 'Reactivo limitante', fr: 'Réactif limitant', it: 'Reagente limitante', de: 'Begrenzendes Reagenz',
}

export function limitingReactantCalculator(params: {
    reactant_a_formula: unknown; reactant_a_grams: unknown; reactant_a_coefficient: unknown
    reactant_b_formula: unknown; reactant_b_grams: unknown; reactant_b_coefficient: unknown
    product_formula: unknown; product_coefficient: unknown; language: unknown
}): { limiting_reactant: string; theoretical_yield_grams: number | string; theoretical_yield_moles: number | string; steps: string } {
    const formulaA = String(params.reactant_a_formula || '')
    const formulaB = String(params.reactant_b_formula || '')
    const productFormula = String(params.product_formula || '')
    const countsA = parseFormula(formulaA)
    const countsB = parseFormula(formulaB)
    const countsProduct = parseFormula(productFormula)

    if (!countsA || !countsB || !countsProduct) {
        const errText = t(INVALID_FORMULA_LABEL, params.language)
        return { limiting_reactant: errText, theoretical_yield_grams: errText, theoretical_yield_moles: errText, steps: errText }
    }

    const massA = molarMassOf(countsA)
    const massB = molarMassOf(countsB)
    const massProduct = molarMassOf(countsProduct)

    const gramsA = Math.max(Number(params.reactant_a_grams) || 0, 0)
    const gramsB = Math.max(Number(params.reactant_b_grams) || 0, 0)
    const coeffA = Math.max(Number(params.reactant_a_coefficient) || 1, 1)
    const coeffB = Math.max(Number(params.reactant_b_coefficient) || 1, 1)
    const coeffProduct = Math.max(Number(params.product_coefficient) || 1, 1)

    const molesA = gramsA / massA
    const molesB = gramsB / massB
    const ratioA = molesA / coeffA
    const ratioB = molesB / coeffB

    const aIsLimiting = ratioA <= ratioB
    const limitingRatio = aIsLimiting ? ratioA : ratioB
    const yieldMoles = limitingRatio * coeffProduct
    const yieldGrams = yieldMoles * massProduct

    const steps = [
        `mol ${formulaA} = ${gramsA} / ${massA.toFixed(3)} = ${molesA.toFixed(4)} mol`,
        `mol ${formulaB} = ${gramsB} / ${massB.toFixed(3)} = ${molesB.toFixed(4)} mol`,
        `${formulaA}: ${molesA.toFixed(4)} / ${coeffA} = ${ratioA.toFixed(4)}`,
        `${formulaB}: ${molesB.toFixed(4)} / ${coeffB} = ${ratioB.toFixed(4)}`,
        `${t(LIMITING_REACTANT_LABEL, params.language)}: ${aIsLimiting ? formulaA : formulaB}`,
        `${productFormula} yield = ${limitingRatio.toFixed(4)} × ${coeffProduct} × ${massProduct.toFixed(3)} = ${yieldGrams.toFixed(4)} g`,
    ].join('\n')

    return {
        limiting_reactant: toSubscriptFormula(aIsLimiting ? formulaA : formulaB),
        theoretical_yield_grams: yieldGrams,
        theoretical_yield_moles: yieldMoles,
        steps,
    }
}
